import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const initialState = {
  sku: "",
  quantity: 0,
  price: 0,
  status: "active"
};

const ProductModal = ({ open, onClose, productId, onSaved }) => {
  const [product, setProduct] = useState(initialState);

  useEffect(() => {
    const loadProduct = async () => {
      if (!open || !productId) {
        setProduct(initialState);
        return;
      }
      try {
        const { data } = await api.get(`/products/${productId}`);
        setProduct({
          sku: data.sku,
          quantity: data.quantity,
          price: Number(data.price) / 100,
          status: data.status
        });
      } catch (err) {
        toastError(err);
        onClose();
      }
    };
    loadProduct();
  }, [open, productId, onClose]);

  const schema = Yup.object().shape({
    sku: Yup.string().trim().required(i18n.t("productModal.validation.sku")),
    quantity: Yup.number()
      .integer(i18n.t("productModal.validation.quantity"))
      .min(0, i18n.t("productModal.validation.quantity"))
      .required(i18n.t("productModal.validation.quantity")),
    price: Yup.number()
      .min(0, i18n.t("productModal.validation.price"))
      .required(i18n.t("productModal.validation.price")),
    status: Yup.string().oneOf(["active", "disabled"]).required()
  });

  const handleSave = async values => {
    const payload = {
      ...values,
      sku: values.sku.trim(),
      quantity: Number(values.quantity),
      price: Math.round(Number(values.price) * 100)
    };
    try {
      if (productId) {
        await api.put(`/products/${productId}`, payload);
      } else {
        await api.post("/products", payload);
      }
      toast.success(i18n.t("productModal.success"));
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t(productId ? "productModal.title.edit" : "productModal.title.add")}
      </DialogTitle>
      <Formik
        initialValues={product}
        enableReinitialize
        validationSchema={schema}
        onSubmit={handleSave}
      >
        {({ touched, errors, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                name="sku"
                label={i18n.t("productModal.form.sku")}
                variant="outlined"
                margin="dense"
                fullWidth
                required
                error={touched.sku && Boolean(errors.sku)}
                helperText={touched.sku && errors.sku}
              />
              <Field
                as={TextField}
                name="quantity"
                type="number"
                label={i18n.t("productModal.form.quantity")}
                variant="outlined"
                margin="dense"
                fullWidth
                required
                inputProps={{ min: 0, step: 1 }}
                error={touched.quantity && Boolean(errors.quantity)}
                helperText={touched.quantity && errors.quantity}
              />
              <Field
                as={TextField}
                name="price"
                type="number"
                label={i18n.t("productModal.form.price")}
                variant="outlined"
                margin="dense"
                fullWidth
                required
                inputProps={{ min: 0, step: "0.01" }}
                error={touched.price && Boolean(errors.price)}
                helperText={touched.price && errors.price}
              />
              <Field
                as={TextField}
                name="status"
                select
                label={i18n.t("productModal.form.status")}
                variant="outlined"
                margin="dense"
                fullWidth
              >
                <MenuItem value="active">{i18n.t("products.status.active")}</MenuItem>
                <MenuItem value="disabled">{i18n.t("products.status.disabled")}</MenuItem>
              </Field>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                {i18n.t("productModal.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting && <CircularProgress size={20} />}
                {i18n.t("productModal.buttons.save")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ProductModal;
